import {
	AddOrderToTruckText,
	AddToTruckText,
	BillToText,
	CancelButtonText,
	CubeCountText,
	ProductLineText,
	RequestDateText,
	SelectOrderDraftText,
	SelectOrderText,
	UnavailableDataPlaceholderText
} from "constants/text";
import { AddTruckOrderModalWrapper } from "./AddTruckOrderModal.styles";
import Close from "@mui/icons-material/Close";
import TruckOrderFormProvider from "../TruckOrderFormProvider";
import { AllFieldsRequired } from "components/NewOrders/constants";
import AutoComplete from "components/Common/Autocomplete/Autocomplete";
import { Controller, FieldValues, useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "stores/application.store";
import { format } from "date-fns";
import { models } from "types/api/viewModels";
import { Button } from "@mui/material";
import { useLazyGetTruckDraftsQuery } from "features/api/truckApi";
import { getCabCubeCountsFromDraft } from "utils/order";
import { epsilonRounding } from "utils/epsilonRounding";
import { displayBillToInfo } from "utils/accounts";
import { findAndReturnBillToInfoFromDraft } from "utils/trucks";
import { useGetTrucksAccountsQuery } from "features/api/accountApi.ts";
import { useEffect } from "react";
import { truckActions } from "features/reducers/truck/truck.ts";
import { useDialog } from "hooks/useDialog.ts";
import { DialogTracking } from "components/Dialog/components/DialogTracking.ts";

const AddTruckOrderModal = () => {
	return (
		<TruckOrderFormProvider>
			<AddTruckOrderModalContent />
		</TruckOrderFormProvider>
	);
};

const AddTruckOrderModalContent = () => {
	const dispatch = useDispatch();
	const { handleSubmit, reset, watch } = useFormContext();
	const truck = useSelector((state: RootState) => state.truck.truck);
	const billTos = useSelector((state: RootState) => state.truck.selectedAccount?.billTos);
	const { data: accounts, isFetching: isAccountsLoading } = useGetTrucksAccountsQuery("");
	const [fetchTruckDrafts, { data: drafts, isFetching: isDraftsLoading }] = useLazyGetTruckDraftsQuery();
	const { closeDialog, dialogIndex, dialogStatus } = useDialog();
	const isOpen = dialogStatus === DialogTracking.ADD_TRUCK_ORDER;
	const handleAddOrderToStop = (values: FieldValues) => {
		dispatch(truckActions.addDraftOrderToStop({ index: dialogIndex ?? 0, draft: values.order }));
		closeDialog();
	};

	useEffect(() => {
		if (isOpen && truck?.truckId) {
			fetchTruckDrafts(truck.truckId);
		}
	}, [fetchTruckDrafts, isOpen, truck?.truckId]);

	const handleClearErrorsOnClose = () => {
		reset();
		closeDialog();
	};

	const handleTruckOrderSubmit = (values: FieldValues) => {
		reset();
		handleAddOrderToStop(values);
	};

	const filterOutAlreadyUsedDrafts = (drafts: models["DraftOrderViewModel"][]) =>
		drafts?.filter((draft) => {
			const allDrafts = truck?.stops?.flatMap((stop) => stop.draftOrderIds);
			return !allDrafts?.includes(draft.draftOrderId);
		});

	const draftOrder: models["DraftOrderViewModel"] = watch("order");

	useEffect(() => {
		const matchingTruckAccount = accounts?.find((account) => account.accountId === truck?.accountId);
		if (matchingTruckAccount) dispatch(truckActions.updateSelectedAccount(matchingTruckAccount));
	}, [accounts, dispatch, truck]);

	return (
		<AddTruckOrderModalWrapper
			open={isOpen}
			onClose={handleClearErrorsOnClose}
			data-testid="truck-stop-add-order-modal"
		>
			<form onSubmit={handleSubmit(handleTruckOrderSubmit)}>
				<div>
					<h6>{AddOrderToTruckText}</h6>
					<Close onClick={handleClearErrorsOnClose} />
				</div>
				<div>
					<span>{AllFieldsRequired}</span>
					<Controller
						name="order"
						render={({ field: { onChange, value = null }, fieldState: { error } }) => (
							<AutoComplete
								options={filterOutAlreadyUsedDrafts(drafts ?? [])}
								onChange={(_, value) => {
									onChange(value);
								}}
								errorText={SelectOrderText}
								isError={!!error}
								label={SelectOrderDraftText}
								dataTestId="truck-order-search"
								value={value}
								isLoading={isDraftsLoading || isAccountsLoading}
								getOptionLabel={(option: models["DraftOrderViewModel"]) =>
									`${option.poNumber ? "PO # " + option.poNumber + " - " : ""}${option.draftName}`
								}
							/>
						)}
					/>
					<div>
						<div>
							<div>
								<span>{RequestDateText}</span>
								<span data-testid="truck-add-stop-order-requestDate">
									{format(truck?.requestedDeliveryDate ?? "", "MM/dd/yyy")}
								</span>
							</div>
							<div>
								<span>{ProductLineText}</span>
								<span data-testid="truck-add-stop-order-productLine">
									{truck?.productLine?.description}
								</span>
							</div>
						</div>
						<div>
							<div>
								<span>{BillToText}</span>
								<span data-testid="truck-add-stop-order-billTo">
									{displayBillToInfo(findAndReturnBillToInfoFromDraft(draftOrder, billTos ?? []))}
								</span>
							</div>
							<div>
								<span>{CubeCountText}</span>
								<span data-testid="truck-add-stop-order-cubeCount">
									{draftOrder
										? epsilonRounding(getCabCubeCountsFromDraft(draftOrder).cubeCount)
										: UnavailableDataPlaceholderText}
								</span>
							</div>
						</div>
					</div>
					<div>
						<Button
							data-testid="truck-order-add-to-truck"
							variant="outlined"
							onClick={handleClearErrorsOnClose}
						>
							{CancelButtonText}
						</Button>
						<Button
							data-testid="truck-order-add-to-truck"
							variant="contained"
							type="submit"
						>
							{AddToTruckText}
						</Button>
					</div>
				</div>
			</form>
		</AddTruckOrderModalWrapper>
	);
};

export default AddTruckOrderModal;
